'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper, {
  type Area,
  type MediaSize,
  type Point,
} from 'react-easy-crop';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Slider } from '@kit/ui/slider';

/** The confirmed reframe: the crop box + the source image's intrinsic
 * dimensions, all in the ORIGINAL image's pixel space. */
export interface MediaCropResult {
  x: number;
  y: number;
  w: number;
  h: number;
  width: number;
  height: number;
}

export interface MediaCropDialogTranslations {
  title?: string;
  description?: string;
  zoom?: string;
  cancel?: string;
  skip?: string;
  confirm?: string;
}

const DEFAULT_TRANSLATIONS: Required<MediaCropDialogTranslations> = {
  title: 'Reframe your photo',
  description:
    'Position and zoom so the full frame shows exactly what you want. The original photo is uploaded unchanged — only the framing is saved.',
  zoom: 'Zoom',
  cancel: 'Cancel',
  skip: 'Use full picture',
  confirm: 'Use this framing',
};

/**
 * Fixed-aspect reframe dialog. Shows the picked image in a react-easy-crop
 * viewport locked to `aspect`, uploads the ORIGINAL file unchanged, and
 * resolves the chosen crop rect (in the image's intrinsic pixel space) plus
 * the intrinsic dimensions. "Use full picture" (`onSkip`) proceeds with no
 * crop — the public hero letterboxes the whole image. Cancelling aborts the
 * upload.
 */
export function MediaCropDialog({
  file,
  aspect,
  onConfirm,
  onSkip,
  onCancel,
  translations: customTranslations,
}: {
  /** The picked file to reframe; the dialog is open while this is non-null. */
  file: File | null;
  aspect: number;
  onConfirm: (result: MediaCropResult) => void;
  /** Proceed with the upload WITHOUT a crop rect (letterbox rendering). */
  onSkip: () => void;
  onCancel: () => void;
  translations?: MediaCropDialogTranslations;
}) {
  const t = { ...DEFAULT_TRANSLATIONS, ...customTranslations };
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [mediaSize, setMediaSize] = useState<MediaSize | null>(null);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);

  // Object URL derives from the picked file (no state, no effect-setState).
  const imageUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file],
  );

  // Revoke-only lifecycle: release the previous URL on change/unmount so the
  // underlying File isn't pinned in memory after the dialog closes.
  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  // Reset the transform when a new file arrives — render-phase state
  // adjustment (the React-sanctioned alternative to setState-in-effect).
  const [lastFile, setLastFile] = useState<File | null>(file);
  if (file !== lastFile) {
    setLastFile(file);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMediaSize(null);
    setAreaPixels(null);
  }

  const onCropComplete = useCallback((_area: Area, areaInPixels: Area) => {
    setAreaPixels(areaInPixels);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!areaPixels || !mediaSize) return;
    const width = Math.round(mediaSize.naturalWidth);
    const height = Math.round(mediaSize.naturalHeight);
    // Integer-round, then fit inside the image with a UNIFORM scale so the
    // aspect ratio survives the clamp (independent per-axis clamping can
    // drift the box outside the finalize validator's 1% ratio tolerance
    // when a sub-pixel overflow lands on one edge only).
    const x = Math.min(Math.max(0, Math.round(areaPixels.x)), width - 1);
    const y = Math.min(Math.max(0, Math.round(areaPixels.y)), height - 1);
    const fit = Math.min(
      (width - x) / areaPixels.width,
      (height - y) / areaPixels.height,
      1,
    );
    const w = Math.max(1, Math.floor(areaPixels.width * fit));
    const h = Math.max(1, Math.floor(areaPixels.height * fit));
    onConfirm({ x, y, w, h, width, height });
  }, [areaPixels, mediaSize, onConfirm]);

  return (
    <Dialog
      open={file !== null}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        <div className="bg-secondary relative aspect-[4/5] w-full overflow-hidden rounded-md">
          {/* restrictPosition (default true) keeps the crop box inside the
              image — the DB all-or-none CHECK requires non-negative,
              in-bounds rects, so letterbox panning must not be reachable.
              Dancers who want the whole picture use "Use full picture". */}
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              objectFit="contain"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              onMediaLoaded={setMediaSize}
            />
          )}
        </div>

        <div className="space-y-1.5">
          <label className="text-muted-foreground text-xs font-medium">
            {t.zoom}
          </label>
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.01}
            onValueChange={(v) => setZoom(v[0] ?? 1)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t.cancel}
          </Button>
          <Button variant="secondary" onClick={onSkip}>
            {t.skip}
          </Button>
          <Button onClick={handleConfirm} disabled={!areaPixels || !mediaSize}>
            {t.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
